import { Stack } from '@mui/material';

import { EntityApprovalStatus, entityApprovalStatus } from '@/api/entity/types';
import AlertIcon from '@/assets/svg/ui-elements/alert-triangle-filled-red.svg';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { Badge, Button, Modal, Separator, Typography } from '@/shared/design-system';

export type EntityStatusAlertModalProps = {
  entityName: string;
  status: Extract<EntityApprovalStatus, 'Blocked' | 'Rejected'>;
  reason?: string | null;
};

export const EntityStatusAlertModal = () => {
  const { open, handleClose, props } = useGlobalModal('ENTITY_STATUS_ALERT_MODAL');

  const isBlocked = props?.status === entityApprovalStatus.Blocked;
  const title = isBlocked ? 'Entity Blocked' : 'Entity Rejected';

  const message = isBlocked
    ? `Your verified Entity "${props?.entityName}" has been blocked by a Gamma Prime administrator. Please reach out to the Gamma Prime team for clarifications.`
    : `Your Entity "${props?.entityName}" has been reviewed and rejected by a Gamma Prime administrator.\nPlease review the Entity details on the Gamma Prime platform for next steps or reach out to the Gamma Prime team for clarifications.`;

  const whatHappensLabel = isBlocked
    ? 'What happens while the Entity remains blocked:'
    : 'What happens to the Entity until it is restored by Gamma Prime:';

  return (
    <Modal open={open} title={title} showClose onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <Stack p="0 16px 16px" gap="24px" flexDirection="column">
        <Separator />

        <Stack gap="16px" flexDirection="column">
          <Badge
            label="IMPORTANT!"
            color="destructive"
            variant="subtle"
            startIcon={<AlertIcon />}
          />

          <Typography variant="body2">{message}</Typography>

          {props?.reason && (
            <Stack flexDirection="column" gap="4px">
              <Typography variant="body2" fontWeight={600}>
                Reason provided by Gamma Prime:
              </Typography>
              <Typography variant="body2">{props.reason}</Typography>
            </Stack>
          )}

          <Typography variant="body2">{whatHappensLabel}</Typography>

          <ul>
            <Typography variant="body2" as="li">
              The Entity is considered suspended.
            </Typography>
            <Typography variant="body2" as="li">
              All the information about Funds linked to this Entity is hidden from public view.
            </Typography>
            <Typography variant="body2" as="li">
              New investments are not allowed for any of the linked funds.
            </Typography>
          </ul>
        </Stack>

        <Button color="tertiary" variant="outline" fullWidth onClick={handleClose}>
          Continue
        </Button>
      </Stack>
    </Modal>
  );
};
